import { Controller } from '@hotwired/stimulus'

export default class FilterAndLoadMoreController extends Controller {
  static targets = ['arrayFilterInput'];

  static values = {
    searchUrl: String,
    storageName: String,
    urlParamMapping: Object
  };

  connect() {
    this.restoreFilters();
    this.orderBy = {};
    this.arrayFilters = {};
    this.initializeFiltersFromUrl();
    this.#restoreArrayFiltersFromInputs();
    this.doSearch(1, false);
  }

  #restoreArrayFiltersFromInputs() {
    this.arrayFilterInputTargets.forEach((input) => {
      if (input.checked) {
        this.#applyArrayFilter(input);
      }
    });
  }

  disconnect() {
    clearTimeout(this.filterDebounceTimeout);
  }

  initializeFiltersFromUrl() {
    if (!this.hasUrlParamMappingValue) return;

    const urlParams = new URLSearchParams(window.location.search);
    Object.entries(this.urlParamMappingValue).forEach(([urlParam, filterKey]) => {
      if (urlParams.has(urlParam)) {
        this.filters[filterKey] = urlParams.get(urlParam);
      }
    });
  }

  loadMore(ev) {
    this.doSearch(ev.currentTarget.dataset.page, true);
  }

  filter(ev) {
    this.#applyFilter(ev.currentTarget);
  }

  filterWithDebounce(ev) {
    clearTimeout(this.filterDebounceTimeout);
    const element = ev.currentTarget;
    this.filterDebounceTimeout = setTimeout(() => {
      this.#applyFilter(element);
    }, 300);
  }

  #applyFilter(element) {
    let value = element.value;

    if (element.type === 'checkbox') {
      value = element.checked;
    }

    this.filters[element.dataset.param] = value;

    if (element.dataset.persist !== 'false') {
      this.saveFilters();
    }

    this.doSearch(1, false);
  }

  order(ev) {
    this.orderBy[ev.currentTarget.dataset.param] = ev.currentTarget.value;

    this.doSearch(1, false);
  }

  stageOrder(ev) {
    this.orderBy[ev.currentTarget.dataset.param] = ev.currentTarget.value;
  }

  toggleArrayFilter(ev) {
    this.#applyArrayFilter(ev.currentTarget);
  }

  #applyArrayFilter(element) {
    const param = element.dataset.param;
    const value = element.value;

    if (!this.arrayFilters[param]) {
      this.arrayFilters[param] = new Set();
    }

    if (element.checked) {
      this.arrayFilters[param].add(value);
    } else {
      this.arrayFilters[param].delete(value);
    }
  }

  applyFilters() {
    this.doSearch(1, false);
  }

  resetFilters() {
    this.arrayFilters = {};
    this.orderBy = {};

    this.element.querySelectorAll("input[data-action*='toggleArrayFilter']").forEach(checkbox => {
      checkbox.checked = false;
    });

    this.element.querySelectorAll("input[type='radio'][data-action*='stageOrder']").forEach((radio, index) => {
      radio.checked = index === 0;
    });

    this.doSearch(1, false);
  }

  doSearch(page, append) {
    const params = {
      page: page,
      append: append,
      ...this.filters,
      ...this.orderBy,
    };

    const url = new URL(this.searchUrlValue);

    Object.entries(params).forEach(([key, value]) => {
      url.searchParams.set(key, value);
    });

    Object.entries(this.arrayFilters).forEach(([key, values]) => {
      values.forEach(value => {
        url.searchParams.append(`${key}[]`, value);
      });
    });

    fetch(url.toString(), {
      headers: { Accept: 'text/vnd.turbo-stream.html' }
    }).then(r => r.text())
      .then(html => Turbo.renderStreamMessage(html));
  }

  saveFilters() {
    const storageKey = this.getStorageKey();
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(this.filters));
    }
  }

  restoreFilters() {
    this.filters = {};
    const storageKey = this.getStorageKey();
    if (storageKey) {
      const savedFilters = localStorage.getItem(storageKey);
      if (savedFilters) {
        this.filters = JSON.parse(savedFilters);
      }
    }
  }

  getStorageKey() {
    return !!this.storageNameValue ? `FilterAndLoadMore[${this.storageNameValue}]` : false;
  }
}
